import { AppRoutes } from 'config/routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useTranslation } from 'react-i18next';
import {
  useCreateSupplyProductionDocumentMutation,
  useGetShipmentTasksSchemeQuery,
  useGetCoreDocumentsNoCacheMutation,
  useSignDocumentMutation,
  useUpdateShipmentTaskMutation,
} from 'store/api/documents';
import { useHandleMutation } from 'utils/handleMutation';
import {
  generateModelFromScheme,
  generateValidationSchema,
  parseSteps,
} from 'utils/sheme-parser';

export const useNewTask = () => {
  const { id: documentId } = useParams();
  const navigate = useNavigate();

  const {
    i18n: { language },
  } = useTranslation();

  const path = useLocation().pathname;
  const { Home, Contracts } = AppRoutes.Dashboard;

  const [step, setStep] = useState<number>(1);

  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string | undefined>();

  const [fetchDocument] = useGetCoreDocumentsNoCacheMutation();

  const [signDocument, signDocumentMutation] = useSignDocumentMutation();

  const { data: shipmentTaskDocumentScheme } = useGetShipmentTasksSchemeQuery(
    {
      documentId: documentId ?? '',
    },
    { skip: !documentId },
  );

  useCreateSupplyProductionDocumentMutation();
  const [updateShipmentTask, updateShipmentTaskMutation] = useUpdateShipmentTaskMutation();

  const dropDownOptions = useMemo(
    () =>
      [
        shipmentTaskDocumentScheme && {
          value: 'ShipmentTask',
          label: 'Задание на поставку',
          scheme: shipmentTaskDocumentScheme,
        },
      ]?.filter((item) => item),
    [shipmentTaskDocumentScheme],
  );

  const [currentSelect, setCurrentSelect] = useState(dropDownOptions[0]);
  const isApproval = path.includes(AppRoutes.Dashboard.Contracts.ContractApproval);

  const handleSign = useCallback(
    async (id?: string, version?: number) => {
      const { data } = (await fetchDocument({
        ids: [documentId ?? contractId ?? ''],
      })) as any;

      const [document] = data ?? [];

      await signDocument({
        signature: null,
        detachedSignature: null,
        documentId: id ?? document?.id ?? '',
        documentVersion: version ?? document?.version ?? 0,
      });
    },
    [fetchDocument, documentId, contractId, signDocument],
  );

  useEffect(() => {
    if (contractId?.length) {
      // handleSign();
    }
  }, [contractId]);

  const onSuccessCreate = useCallback(async (id: string) => {
    setContractId(id);
    handleClose();
  }, []);

  const handleClose = useCallback(() => {
    navigate(Home + AppRoutes.Dashboard.ShipmentTasks.Home);
  }, [navigate]);

  const onSuccessSign = useCallback(async () => {
    setIsProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 5000));
    setIsProcessing(false);
    handleClose();
  }, [handleClose]);

  const onErrorSign = useCallback((err: any) => {
    console.error('Error sign', err);
  }, []);

  useHandleMutation({
    ...updateShipmentTaskMutation,
    onSuccess: onSuccessCreate,
  });

  useHandleMutation({
    ...signDocumentMutation,
    onSuccess: onSuccessSign,
    onError: onErrorSign,
  });

  useEffect(() => {
    setCurrentSelect(dropDownOptions[0]);
  }, [dropDownOptions, shipmentTaskDocumentScheme]);

  const { currentScheme, model, steps, stepsLabels } = useMemo(() => {
    return {
      currentScheme: currentSelect?.scheme,
      model: generateModelFromScheme(currentSelect?.scheme, language),
      ...parseSteps(currentSelect?.scheme),
    };
  }, [currentSelect]);

  const getValidationSchema = () => {
    if (isApproval) {
      return Yup.object().shape({});
    }
    return generateValidationSchema(currentScheme, step);
  };

  return {
    step,
    steps,
    model,
    setStep,
    handleSign,
    isApproval,
    stepsLabels,
    isProcessing,
    handleClose,
    currentSelect,
    currentScheme,
    dropDownOptions,
    setCurrentSelect,
    updateShipmentTask,
    getValidationSchema,
    isLoading: signDocumentMutation.isLoading || updateShipmentTaskMutation.isLoading,
  };
};
